import React, { ReactElement, useEffect, useState } from 'react'
import { Link as ReactRouterLink, useLocation } from 'react-router-dom'

import cx from 'classnames'

import { Flex, Header, Icon, IconButton, Link, Paper } from '@driftt/dds'
import { faArrowUpRightFromSquare } from '@driftt/dds.icons/light/faArrowUpRightFromSquare'
import { faChevronLeft } from '@driftt/dds.icons/light/faChevronLeft'
import { faChevronRight } from '@driftt/dds.icons/light/faChevronRight'
import { access } from '@driftt/traffic-control'

import { activityPath, emailDashboardPath, liveViewPath, rootPath } from 'utils/paths'

export enum MenuItems {
  'PROSPECTOR',
  'EMAIL',
  'ACTIVITY',
  'OTHER'
}

const MenuItem = ({
  children,
  path,
  isActive,
  target,
  onClick
}: {
  children: string | ReactElement | ReactElement[]
  path: string
  isActive?: boolean
  target?: string
  onClick?: () => void
}) => (
  <Link
    as={ReactRouterLink}
    className={cx(isActive ? 'active' : '')}
    css={{
      padding: '8px',
      fontWeight: 400,
      color: '$gray11',
      width: '100%',
      '&:hover': {
        backgroundColor: '$gray2',
        color: '$gray11',
        textDecoration: 'none'
      },
      '&.active': {
        backgroundColor: '$gray2',
        color: '$blue8',
        fontWeight: 600
      }
    }}
    to={{ pathname: path }}
    target={target}
    onClick={onClick}>
    {children}
  </Link>
)

const localStorageKey = 'PROSPRECTOR_SUBNAV_CLOSED'

export const Subnav = () => {
  const [activeItem, setActiveItem] = useState<MenuItems>(MenuItems.PROSPECTOR)
  const [isClosed, setIsClosed] = useState<boolean>(
    localStorage.getItem(localStorageKey) === 'true'
  )
  const location = useLocation()

  useEffect(() => {
    switch (location.pathname) {
      case activityPath:
        setActiveItem(MenuItems.ACTIVITY)
        break
      case emailDashboardPath:
        setActiveItem(MenuItems.EMAIL)
        break
      case rootPath:
        setActiveItem(MenuItems.PROSPECTOR)
        break
      default:
        setActiveItem(MenuItems.OTHER)
        break
    }
  }, [location, location.pathname])

  const onClose = () => {
    setIsClosed(!isClosed)
    localStorage.setItem(localStorageKey, `${!isClosed}`)
  }

  const hasEmailDashboardEnabled = access.hasGates('automated_email_dashboard')

  return (
    <Paper
      elevation={2}
      css={{
        marginLeft: isClosed ? '-176px' : '0',
        height: '100vh',
        padding: '8px',
        transition: 'margin-left .3s ease-out',
        zIndex: 1000
      }}>
      <IconButton
        icon={isClosed ? faChevronRight : faChevronLeft}
        title={'close navigation'}
        size="small"
        css={{
          position: 'absolute',
          marginLeft: '180px',
          marginTop: '24px',
          zIndex: 1200,
          backgroundColor: 'white',
          border: 'solid 1px $gray2',
          borderRadius: '10px',
          boxShadow: 'var(--drift-dds-shadows-1)'
        }}
        onClick={onClose}
      />
      <Flex fullHeight direction="column" gap="half" css={{ width: '184px' }}>
        <Header variant="h2" css={{ padding: '4px', marginBottom: '24px', marginTop: '16px' }}>
          Prospector
        </Header>
        <MenuItem
          isActive={activeItem === MenuItems.PROSPECTOR}
          path={rootPath}
          onClick={() => setActiveItem(MenuItems.PROSPECTOR)}>
          Prospector dashboard
        </MenuItem>
        {hasEmailDashboardEnabled && (
          <MenuItem
            isActive={activeItem === MenuItems.EMAIL}
            path={emailDashboardPath}
            onClick={() => setActiveItem(MenuItems.EMAIL)}>
            Email dashboard
          </MenuItem>
        )}
        <MenuItem
          isActive={activeItem === MenuItems.ACTIVITY}
          path={activityPath}
          onClick={() => setActiveItem(MenuItems.ACTIVITY)}>
          Account activity
        </MenuItem>
        <MenuItem path={liveViewPath} target="_blank">
          <Flex alignItems="center">
            Live view
            <Icon icon={faArrowUpRightFromSquare} />
          </Flex>
        </MenuItem>
      </Flex>
    </Paper>
  )
}
